import { 
  Home, Tent, Caravan, BedDouble, ShowerHead, Accessibility,
  ParkingSquare, UtensilsCrossed, Flame, Wifi, Zap, Beer, Star, Moon, Coffee
} from 'lucide-react';
import { FACILITIES_MAP, getFacilityStarRating, getHospitalityStarRating, hasOvernight } from '../utils/facilities';
import { useLanguage } from '../contexts/LanguageContext';

const ICON_MAP = {
  home: Home, tent: Tent, caravan: Caravan, bed: BedDouble,
  shower: ShowerHead, accessibility: Accessibility, bike: ParkingSquare,
  utensils: UtensilsCrossed, flame: Flame, wifi: Wifi, zap: Zap, beer: Beer,
};

export const FacilityIcon = ({ facilityId, size = 16, className = '' }) => {
  const facility = FACILITIES_MAP[facilityId];
  if (!facility) return null;
  const IconComp = ICON_MAP[facility.icon];
  if (!IconComp) return null;
  return <IconComp style={{ width: size, height: size }} className={className} />;
};

export const FacilityBadge = ({ facilityId, compact = false }) => {
  const { t } = useLanguage();
  const facility = FACILITIES_MAP[facilityId];
  if (!facility) return null;
  const IconComp = ICON_MAP[facility.icon];
  if (!IconComp) return null;
  const label = t(`facilities.${facilityId}`) || facility.label;
  if (compact) {
    return (
      <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-emerald-500/10" title={label}>
        <IconComp className="w-3.5 h-3.5 text-emerald-400" />
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-300" data-testid={`facility-${facilityId}`}>
      <IconComp className="w-4 h-4" />
      <span className="text-xs font-medium">{label}</span>
    </div>
  );
};

/** Facility stars (1-5) - based on number of facilities */
export const FacilityStarRating = ({ facilities, size = 'sm' }) => {
  const rating = getFacilityStarRating(facilities);
  if (rating === 0) return null;
  const starSize = size === 'sm' ? 'w-3 h-3' : size === 'md' ? 'w-4 h-4' : 'w-5 h-5';
  return (
    <div className="flex items-center gap-0.5" data-testid="facility-star-rating" title={`${rating}/5 facilitetsstjerner`}>
      {[1,2,3,4,5].map(i => (
        <Star key={i} className={`${starSize} ${i <= rating ? 'text-emerald-400 fill-emerald-400' : 'text-zinc-700'}`} />
      ))}
    </div>
  );
};

/** Hospitality stars (1-3) - based on overnight + breakfast */
export const HospitalityStarRating = ({ facilities, overnightFree, breakfastIncluded, breakfastPrice, size = 'sm' }) => {
  const rating = getHospitalityStarRating(facilities, overnightFree, breakfastIncluded, breakfastPrice);
  if (rating === 0) return null;
  const starSize = size === 'sm' ? 'w-3 h-3' : size === 'md' ? 'w-4 h-4' : 'w-5 h-5';
  const labels = { 1: 'Betalt overnatning', 2: 'Gratis overnatning', 3: 'Gratis overnatning + morgenmad' };
  return (
    <div className="flex items-center gap-0.5" data-testid="hospitality-star-rating" title={labels[rating]}>
      {[1,2,3].map(i => (
        <Star key={i} className={`${starSize} ${i <= rating ? 'text-amber-400 fill-amber-400' : 'text-zinc-700'}`} />
      ))}
    </div>
  );
};

/** Combined display: facility stars (green) + hospitality stars (gold) */
export const StarRating = ({ facilities, overnightFree, breakfastIncluded, breakfastPrice, size = 'sm' }) => {
  const facRating = getFacilityStarRating(facilities);
  const hospRating = getHospitalityStarRating(facilities, overnightFree, breakfastIncluded, breakfastPrice);
  if (facRating === 0 && hospRating === 0) return null;
  return (
    <div className="flex items-center gap-2" data-testid="star-rating">
      {facRating > 0 && <FacilityStarRating facilities={facilities} size={size} />}
      {hospRating > 0 && <HospitalityStarRating facilities={facilities} overnightFree={overnightFree} breakfastIncluded={breakfastIncluded} breakfastPrice={breakfastPrice} size={size} />}
    </div>
  );
};

export const OvernightBadge = ({ facilities }) => {
  const { t } = useLanguage();
  if (!hasOvernight(facilities)) return null;
  return (
    <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-500/15 text-indigo-300 text-[10px] font-semibold" data-testid="overnight-badge">
      <Moon className="w-3 h-3" />
      {t('map.overnight')}
    </div>
  );
};

export const OvernightPriceBadge = ({ overnightFree, overnightPrice, facilities }) => {
  const { t } = useLanguage();
  if (!hasOvernight(facilities)) return null;
  if (overnightFree) {
    return (
      <span className="px-2 py-0.5 rounded-full bg-green-500/15 text-green-400 text-[10px] font-semibold" data-testid="overnight-price-badge">
        {t('map.overnightFree')}
      </span>
    );
  }
  if (overnightPrice && overnightPrice > 0) {
    return (
      <span className="px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 text-[10px] font-semibold" data-testid="overnight-price-badge">
        {overnightPrice} kr/nat
      </span>
    );
  }
  return null;
};

export const BreakfastBadge = ({ breakfastIncluded, breakfastPrice, facilities }) => {
  const { t } = useLanguage();
  if (!hasOvernight(facilities) || !breakfastIncluded) return null;
  const isFree = !breakfastPrice || breakfastPrice === 0;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
      isFree ? 'bg-green-500/15 text-green-400' : 'bg-amber-500/15 text-amber-300'
    }`} data-testid="breakfast-badge">
      <Coffee className="w-3 h-3" />
      {isFree ? t('map.freeBreakfast') : t('map.breakfastPrice', { price: breakfastPrice })}
    </span>
  );
};

export default FacilityIcon;
